// src/components/OrderTable.js
import React, { useState, useEffect  } from 'react';
import { Table, Tooltip, OverlayTrigger, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ClientInfoRow from './ClientInfoRow';
import ItemsInfoRow from '../Global/ItemsInfoRow';
import InfoToggle from '../Global/InfoToggle';
import DeliveryDateEditor from '../Global/DeliveryDateEditor';  
import CarrierAssigner from '../Global/CarrierAssigner'; 
import '../Global/Style/OrderTable.css';
import CommentsInfoRow from '../Global/CommentsInfoRow';
import EditItemsInOrder from '../Global/EditItemsInOrder';
import EditClientInOrder from '../Global/EditClientInOrder';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { handleDownloadPhotos } from '../Global/DownloadUtils';
import ActionButtons from './ActionButtons';  
import { isPropietarioOrAdminOrGestion, isComercialAndNotOwnerAdminGestion, getSalesRepId, isJefeComerciales, isJefeTransportistas, isComercial, isPropietarioOrAdminOrGestionOrEspectador } from '../../services/AuthService';
import { getOrderById } from '../../api';
import IncidentForm from '../IncidentForm';
import TimeSlotAssigner from '../Global/TimeSlotAssigner';
import ClientOriginAssigner from '../Global/ClientOriginAssigner';
import PaymentMethodAssigner from '../Global/PaymentMethodAssigner';
import SortableHeader from '../ui/SortableHeader';
import { getContrastTextColor } from '../../utils/ColorUtils';

const OrderTable = (props) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({});
  const [showPhotos, setShowPhotos] = useState({});
  const [visibility, setVisibility] = useState({
    incidentVisibility: {},
    commentVisibility: {},
    deliveryVisibility: {},
  });
  const [editRow, setEditRow] = useState(null);
  const [editRowPrepaidAmount, setEditRowPrepaidAmount] = useState(0);
  const [editClientRow, setEditClientRow] = useState(null); 
  const [detailedItems, setDetailedItems] = useState({});
  const [editedOrderItems, setEditedOrderItems] = useState({});
  const [reRenderKey, setReRenderKey] = useState(0);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [massDate, setMassDate] = useState(null);
  const currentSalesRepId = Number(getSalesRepId());
  const [incidentRow, setIncidentRow] = useState(null);
  const loadOrderItems = async (orderId) => {
    const order = props.orders?.find(o => o.id === orderId);
    if (order?.items != null) return;

    try {
      const orderData = await getOrderById(orderId);
      props.setOrders(prevOrders =>
        prevOrders.map(o =>
          o.id === orderId
            ? { ...o, items: orderData.items, specialProducts: orderData.specialProducts }
            : o
        )
      );
    } catch (error) {
      console.error('Error loading order items:', error);
    }
  };

  const handlePhotos = async (orderId) => {
    if (!photos[orderId]) {
      const fetchedPhotos = await handleDownloadPhotos(orderId);
      setPhotos((prevPhotos) => ({
        ...prevPhotos,
        [orderId]: fetchedPhotos,
      }));
    }

    setShowPhotos((prevState) => ({
      ...prevState,
      [orderId]: !prevState[orderId],
    }));
  };



  const handleCarrierChange = (orderIds, newCarrier) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const updatedOrders = props.orders.map(order => {
      if (ids.includes(order.id)) {
        return { ...order, carrier: newCarrier, carrierId: newCarrier?.id ?? null };
      }
      return order;
    });
    props.setOrders(updatedOrders);
  };

  const handleTimeSlotChange = (orderIds, newTimeSlot) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const updatedOrders = props.orders.map(order => {
      if (ids.includes(order.id)) {
        return { ...order, timeSlot: newTimeSlot, timeSlotId: newTimeSlot?.valueId ?? 0 };
      }
      return order;
    });
    props.setOrders(updatedOrders);
    props.reloadPage()
  };

  const handleClientOriginChange = (orderIds, newClientOrigin) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const updatedOrders = props.orders.map(order => {
      if (ids.includes(order.id)) {
        return { ...order, clientOrigin: newClientOrigin, clientOriginId: newClientOrigin?.valueId ?? null };
      }
      return order;
    });
    props.setOrders(updatedOrders);
    props.reloadPage()
  };

  const handlePaymentMethodChange = (orderIds, newPaymentMethod) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const updatedOrders = props.orders.map(order => {
      if (ids.includes(order.id)) {
        return { ...order, paymentMethod: newPaymentMethod, paymentMethodId: newPaymentMethod?.valueId ?? null };
      }
      return order;
    });
    props.setOrders(updatedOrders);
    props.reloadPage()
  };

  const handleToggleVisibility = (type, orderId) => {
    setVisibility((prevState) => ({
      ...prevState,
      [type]: {
        ...prevState[type],
        [orderId]: !prevState[type][orderId],
      },
    }));
  };

  const toggleEditRow = async (orderId) => {
    if (editRow !== orderId) {
      await loadOrderItems(orderId);
    }
    setEditRow((prevRow) => (prevRow === orderId ? null : orderId));
  };

  const toggleIncidentRow = (orderId) => {
    setIncidentRow((prevRow) => (prevRow === orderId ? null : orderId));
  };

  const toggleEditClientRow = (orderId) => {
    setEditClientRow((prevRow) => (prevRow === orderId ? null : orderId));
  };


  const handleItemsChange = (updatedItems) => {
    setEditedOrderItems(updatedItems);
  };

  return (
    <div style={{ overflowX: 'auto', width: '100%' }} data-testid="order-table-container">
      <div className="mb-3">
      {(isPropietarioOrAdminOrGestion() || isJefeTransportistas()) && (
        <button
          className={`btn ${multiSelectMode ? 'btn-secondary' : 'btn-primary'} btn-sm`}
          onClick={() => {
            setMultiSelectMode(!multiSelectMode);
            setSelectedOrders([]);
          }}
          data-testid="multi-select-toggle-button"
        >
          {multiSelectMode ? 'Cancelar selección múltiple' : 'Editar varios pedidos'}
        </button>
      )}
      </div>
      {multiSelectMode && selectedOrders.length > 0 && (
        <div className="d-flex gap-3 align-items-end mb-3">
          <CarrierAssigner
            order={props.orders.find(o => o.id === selectedOrders[0])}
            onCarrierChange={handleCarrierChange}
            multiSelect={true}
            selectedOrderIds={selectedOrders}
          />
          {isPropietarioOrAdminOrGestion() && (
            <DeliveryDateEditor
              order={{ deliveryDate: massDate }}
              onDateChange={handleCarrierChange}  // usa el mismo handle para actualizar localmente
              multiSelect={true}
              selectedOrderIds={selectedOrders}
            />
          )}
        </div>
      )}

      <Table striped bordered hover data-testid="orders-table" style={{ minWidth: '1200px', width: 'max-content' }}>
        <thead>
          <tr className="table-dark" data-testid="table-header">
            {multiSelectMode && <th data-testid="checkbox-header"></th>}
            <SortableHeader column="externalId" onSort={props.onSort} getSortIcon={props.getSortIcon}>ID</SortableHeader>
            <SortableHeader column="address.zipCode" onSort={props.onSort} getSortIcon={props.getSortIcon}>CP</SortableHeader>
            <SortableHeader column="address.city" onSort={props.onSort} getSortIcon={props.getSortIcon}>Ciudad</SortableHeader>
            <SortableHeader column="client.name" onSort={props.onSort} getSortIcon={props.getSortIcon}>Cliente</SortableHeader>
            {(isPropietarioOrAdminOrGestionOrEspectador() || isJefeComerciales()) && <SortableHeader column="salesRep.name" onSort={props.onSort} getSortIcon={props.getSortIcon}>Comercial</SortableHeader>}
            <SortableHeader column="orderDate" onSort={props.onSort} getSortIcon={props.getSortIcon}>Fecha</SortableHeader>
            <SortableHeader column="deliveryDate" onSort={props.onSort} getSortIcon={props.getSortIcon}>Fecha Entrega</SortableHeader>
            <th>Horario Entrega</th>
            <th>Origen Cliente</th>
            <th>Método Pago</th>
            <SortableHeader column="totalAmount" onSort={props.onSort} getSortIcon={props.getSortIcon}>Total</SortableHeader>
            <th>Estado</th>
            {!isComercialAndNotOwnerAdminGestion() && <SortableHeader column="carrier.firstName" onSort={props.onSort} getSortIcon={props.getSortIcon}>Transportista</SortableHeader>}
            <th>Productos</th>
            <th>Acciones</th>
            <th>Comentarios</th>
          </tr>
        </thead>
        <tbody>
          {props.orders == null && (
            <tr data-testid="loading-row">
              <td colSpan={multiSelectMode ? 17 : 16} className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando pedidos…
              </td>
            </tr>
          )}

           {Array.isArray(props.orders) && props.orders.length === 0 && (
            <tr data-testid="no-orders-row">
              <td colSpan={multiSelectMode ? 17 : 16} className="text-center py-4">
                No se han encontrado pedidos
              </td>
            </tr>
          )}

          {Array.isArray(props.orders) &&
            props.orders.length > 0 &&
            props.orders.map((order) => {
              const clientName = `${order.client?.name || ''} ${order.client?.lastName1 || ''}`.trim();
              const deliveryAddress = order.address ? `${order.address.zipCode || ''} ${order.address.city || ''}`.trim() : '';
              const clientDisplayName = deliveryAddress ? `${clientName} CP:${deliveryAddress}` : clientName;
              const totalItems = (order.itemCount || 0) + (order.specialProductCount || 0);
              const totalQuantity = (order.items || []).reduce((acc, item) => acc + item.quantity, 0) + 
                                   (order.specialProducts || []).reduce((acc, item) => acc + (item.quantity || 1), 0);
              const productsLabel = totalItems > 0
                ? `${totalItems} ${totalItems > 1 ? 'Productos' : 'Producto'}`
                : 'Sin productos';
              const isDelivered = order.deliveredTime !== null;
              const isReadonly = !isPropietarioOrAdminOrGestion() && !isJefeComerciales;
              const isFinalStatus = isDelivered || order.hasIncident;

              return (
                <React.Fragment key={`${order.id}-${reRenderKey}`}>
                  <tr 
                    data-testid={`order-row-${order.id}`}
                    style={{
                      backgroundColor: order.isImportant 
                        ? '#fff3cd' 
                        : order.hasIncident 
                        ? '#ffebee' 
                        : 'inherit',
                      borderLeft: order.isImportant 
                        ? '5px solid #ff6b35' 
                        : order.hasIncident 
                        ? '5px solid #f44336' 
                        : 'none',
                      boxShadow: order.isImportant 
                        ? '0 2px 8px rgba(255, 107, 53, 0.3)' 
                        : order.hasIncident 
                        ? '0 2px 4px rgba(244, 67, 54, 0.2)' 
                        : 'none',
                      fontWeight: order.isImportant ? 'bold' : 'normal'
                    }}
                  >
                    {multiSelectMode && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          disabled={isFinalStatus}
                          onChange={(e) => {
                            setSelectedOrders((prev) =>
                              e.target.checked ? [...prev, order.id] : prev.filter((id) => id !== order.id)
                            );
                          }}
                          data-testid={`order-checkbox-${order.id}`}
                        />
                      </td>
                    )}
                    <td data-testid={`order-id-${order.id}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {!isReadonly && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-toggle-important">{order.isImportant ? 'Clic para desmarcar como importante' : 'Clic para marcar como importante'}</Tooltip>}
                          >
                            <div
                              style={{
                                background: order.isImportant 
                                  ? 'linear-gradient(45deg, #ff6b35, #f7931e)' 
                                  : 'linear-gradient(45deg, #ccc, #999)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                animation: order.isImportant ? 'pulse 2s infinite' : 'none',
                                cursor: 'pointer',
                                opacity: order.isImportant ? 1 : 0.7
                              }}
                              onClick={async () => {
                                const newImportantState = !order.isImportant;
                                try {
                                  await props.onUpdateOrderImportance(order.id, newImportantState);
                                } catch (error) {
                                  console.error('Error updating order importance:', error);
                                }
                              }}
                              data-testid={`order-important-toggle-${order.id}`}
                            >
                              ⭐
                            </div>
                          </OverlayTrigger>
                        )}
                        {isReadonly && order.isImportant && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-important">🔥 PEDIDO IMPORTANTE 🔥</Tooltip>}
                          >
                            <div
                              style={{
                                background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                animation: 'pulse 2s infinite'
                              }}
                              data-testid={`order-important-badge-${order.id}`}
                            >
                              ⭐
                            </div>
                          </OverlayTrigger>
                        )}
                        {order.parentId !== null ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-top">Este pedido proviene de una incidencia</Tooltip>}
                          >
                            <span
                              style={{
                                color: 'red',
                                fontWeight: 'bold',
                              }}
                              data-testid={`order-external-id-incident-${order.id}`}
                            >
                              {order.externalId || 'N/A'}
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <span data-testid={`order-external-id-${order.id}`}>
                            {order.externalId || 'N/A'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-testid={`order-zipcode-${order.id}`}>{order.address?.zipCode || 'N/A'}</td>
                    <td data-testid={`order-city-${order.id}`}>{order.address?.city || 'N/A'}</td>
                    <td data-testid={`order-client-${order.id}`}>
                      <InfoToggle
                        label={clientName}
                        openState={props.openClients}
                        handleToggle={props.handleClientToggle}
                        id={order.id}
                      />
                    </td>
                    {(isPropietarioOrAdminOrGestionOrEspectador() || isJefeComerciales()) && (
                      <td
                        style={{
                          backgroundColor: order.salesRep?.colourHex ?? 'inherit',
                          color: getContrastTextColor(order.salesRep?.colourHex),
                          textAlign: 'center',
                        }}
                      >
                        {order.salesRep ? `${order.salesRep.name} ${order.salesRep.lastName1}` : 'Sin Asignar'}
                      </td>
                    )}
                    <td data-testid={`order-date-${order.id}`}>{new Date(order.orderDate).toLocaleString('es-ES')}</td>
                    <td data-testid={`delivery-date-${order.id}`}>
                      <DeliveryDateEditor
                        order={order}
                      />
                    </td>
                    <td style={{textAlign: 'center'}} data-testid={`time-slot-${order.id}`}>
                      <TimeSlotAssigner order={order} onTimeSlotChange={handleTimeSlotChange} preChargedTimeSlots={props.timeSlots}/>
                    </td>
                    <td style={{textAlign: 'center'}} data-testid={`client-origin-${order.id}`}>
                      <ClientOriginAssigner order={order} onClientOriginChange={handleClientOriginChange} preChargedClientOrigins={props.clientOrigins}/>
                    </td>
                    <td style={{textAlign: 'center'}} data-testid={`payment-method-${order.id}`}>
                      <PaymentMethodAssigner order={order} onPaymentMethodChange={handlePaymentMethodChange} preChargedPaymentMethods={props.paymentMethods}/>
                    </td>
                    <td data-testid={`order-amount-${order.id}`}>
                      {order.alreadyPaid > 0 ? (
                        <div style={{ whiteSpace: 'normal', lineHeight: '1.5' }}>
                          <div data-testid={`total-amount-${order.id}`}>
                            <strong>Total: </strong>
                            {order.totalAmount !== undefined && order.totalAmount !== null
                              ? order.totalAmount.toFixed(2)
                              : 'N/A'}€
                          </div>
                          <div data-testid={`paid-amount-${order.id}`}>
                            <strong>Pagado: </strong>
                            {order.alreadyPaid !== undefined && order.alreadyPaid !== null
                              ? order.alreadyPaid.toFixed(2)
                              : 'N/A'}€
                          </div>
                          <div data-testid={`pending-amount-${order.id}`}>
                            <strong>Pendiente: </strong>
                            {order.pendingAmount !== undefined && order.pendingAmount !== null
                              ? order.pendingAmount.toFixed(2)
                              : 'N/A'}€
                          </div>
                        </div>
                      ) : (
                        <div data-testid={`simple-total-${order.id}`}>
                          {order.totalAmount !== undefined && order.totalAmount !== null
                            ? order.totalAmount.toFixed(2)
                            : 'N/A'}€
                        </div>
                      )}
                    </td>
                    <td data-testid={`order-status-${order.id}`}>
                      {order.hasIncident && !isDelivered ? (
                        <InfoToggle
                          label="INCIDENCIA"
                          openState={{ [order.id]: visibility.incidentVisibility[order.id] }}
                          handleToggle={() => handleToggleVisibility('incidentVisibility', order.id)}
                          id={order.id}
                          color="red"
                        />
                      ) : null}
                      {visibility.incidentVisibility[order.id] && order.hasIncident && (
                        <div data-testid={`incident-details-${order.id}`}>
                          <p><strong>Razón: </strong>{order.incidentType?.name || 'N/A'}</p>
                          <p><strong>Acción: </strong>{order.incidentAction?.name || 'N/A'}</p>
                        </div>
                      )}
                      {isDelivered ? (
                        <InfoToggle
                          label="Entregado"
                          openState={{ [order.id]: visibility.deliveryVisibility[order.id] }}
                          handleToggle={() => handleToggleVisibility('deliveryVisibility', order.id)}
                          id={order.id}
                        />
                      ) : null}
                      {visibility.deliveryVisibility[order.id] && isDelivered && (
                        <div data-testid={`delivery-details-${order.id}`}>
                          <p><strong>Fecha de Firma: </strong>{order.signedTime ? new Date(order.signedTime).toLocaleString('es-ES') : 'N/A'}</p>
                          <p><strong>Entregado: </strong>{new Date(order.deliveredTime).toLocaleString('es-ES')}</p>
                        </div>
                      )}
                      {!order.hasIncident && !isDelivered && <span data-testid={`simple-status-${order.id}`}>{order.status || 'N/A'}</span>}
                    </td>
                    {!isComercialAndNotOwnerAdminGestion() && (
                      <td
                        style={{
                          backgroundColor: order.carrier && order.carrier.colourHex ? order.carrier.colourHex : 'initial', // Color del carrier
                          color: order.carrier && order.carrier.colourHex ? getContrastTextColor(order.carrier.colourHex) : 'initial', // Asegúrate de que el color del texto sea adecuado
                          textAlign: 'center',
                        }}
                      >
                        <CarrierAssigner order={order} onCarrierChange={handleCarrierChange} />
                      </td>
                    )}
                    
                    <td 
                      data-testid={`order-products-${order.id}`}
                      style={{
                        backgroundColor: order.specialProductCount > 0 ? '#f8f9fa' : 'inherit'
                      }}
                    >
                      <InfoToggle
                        label={`${productsLabel}${order.specialProductCount > 0 ? '*' : ''}`}
                        openState={props.openOrderItems}
                        handleToggle={async (orderId) => {
                          if (!props.openOrderItems[orderId]) {
                            await loadOrderItems(orderId);
                          }
                          props.handleOrderItemToggle(orderId);
                        }}
                        id={order.id}
                        showChevron={totalItems > 0}
                      />
                      {order.totalScore > 0 && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#6c757d', 
                          marginTop: '2px',
                          fontStyle: 'italic'
                        }}>
                          📊 {order.totalScore} pts
                        </div>
                      )}
                    </td>
                    <td style={{ width: '90px', textAlign: 'center' }} data-testid={`order-actions-${order.id}`}>
                      <ActionButtons
                        order={order}
                        navigate={navigate}
                        toggleEditRow={toggleEditRow}
                        toggleEditClientRow={toggleEditClientRow}
                        handlePhotos={handlePhotos}
                        readOnly={isReadonly}
                        toggleIncidentRow={toggleIncidentRow}
                      />
                    </td>
                    <td data-testid={`order-comments-${order.id}`}>
                      <InfoToggle
                        label={`Comentarios (${[order.internalComments, order.externalComments, order.deliveryComments].filter(comment => comment && comment.trim() !== '').length})`}
                        openState={{ [order.id]: visibility.commentVisibility[order.id] }}
                        handleToggle={() => handleToggleVisibility('commentVisibility', order.id)}
                        id={order.id}
                      />
                    </td>
                  </tr>
                  {editRow === order.id && (
                    <tr data-testid={`edit-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16} className="edit-row">
                        <EditItemsInOrder
                          initialItems={order.items || []}
                          initialSpecialProducts={order.specialProducts || []}
                          onItemsChange={(items, specialProducts) => handleItemsChange({ items, specialProducts })}
                          onOrderMileageChange={props.handleOrderMileageChange}
                          initialMileage={order.mileage}
                          initialPrepaidAmount={order.alreadyPaid > 0 ? order.alreadyPaid : null}
                          onPrepaidAmountChange={(val) => setEditRowPrepaidAmount(val)}
                          onSave={async (items, specialProducts, mileage) => {
                            await props.handleSaveEdit(order.id, { items, specialProducts, mileage }, editRowPrepaidAmount);
                            setEditRow(null);
                          }}
                          onCancel={() => setEditRow(null)}
                          saveLabel="Guardar Cambios"
                        />
                      </td>
                    </tr>
                  )}

                  {editClientRow === order.id && (
                    <EditClientInOrder
                      order={order}
                      onSaved={() => {
                        setEditClientRow(null);
                        props.onClientSaved();
                      }}
                      onCancel={() => setEditClientRow(null)}
                    />
                  )}

                  {incidentRow === order.id && (
                    <tr data-testid={`incident-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16}>
                        <div className="p-3 border rounded bg-light" data-testid={`incident-form-container-${order.id}`}>
                          <h5 className="mb-3" data-testid={`incident-form-title-${order.id}`}>
                            <i className="bi bi-exclamation-circle text-danger me-2"></i>
                            Registrar Incidencia
                          </h5>
                          <IncidentForm orderId={order.id} initialMileage={order.mileage} />
                        </div>
                      </td>
                    </tr>
                  )}

                  {showPhotos[order.id] && photos[order.id] && (
                    <tr data-testid={`photos-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16} className="text-center">
                        <div className="photos-preview" data-testid={`photos-container-${order.id}`}>
                          {photos[order.id].map((photo, index) => (
                            <img
                              key={index}
                              src={`data:image/jpeg;base64,${photo}`}
                              alt={`Foto ${index + 1}`}
                              className="img-thumbnail"
                              style={{ width: '200px', margin: '5px' }}
                              data-testid={`photo-${order.id}-${index}`}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  {visibility.commentVisibility[order.id] && (
                    <tr data-testid={`comments-info-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16}>
                        <CommentsInfoRow order={order} />
                      </td>
                    </tr>
                  )}
                  {props.openClients[order.id] && (
                    <tr data-testid={`client-info-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16} className="client-info-row">
                        <ClientInfoRow order={order} />
                      </td>
                    </tr>
                  )}
                  {props.openOrderItems[order.id] && (
                    <tr data-testid={`items-info-row-${order.id}`}>
                      <td colSpan={multiSelectMode ? 17 : 16} className="items-info-row">
                        <ItemsInfoRow order={order} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </Table>
    </div>
  );
};

export default OrderTable;
